// Iter 83.5 / Phase E4 Sprint 5 — Asset Return certificate print.
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const fmtMoney = (n) =>
    `₹ ${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const OUTCOME_TONE = {
    Good: "bg-emerald-100 text-emerald-800",
    Damaged: "bg-amber-100 text-amber-800",
    Lost: "bg-rose-100 text-rose-800",
    NotReturned: "bg-rose-100 text-rose-800",
};

export default function AssetReturnPrint() {
    const { assignmentId } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data: d } = await api.get(`/print/asset-return/${assignmentId}`);
                setData(d);
            } catch (err) {
                setError(formatApiError(err, "Failed to load return certificate"));
                toast.error(formatApiError(err, "Load failed"));
            }
        })();
    }, [assignmentId]);

    if (error) return <div className="p-12 text-center text-rose-600" data-testid="asset-return-error">{error}</div>;
    if (!data) return <div className="p-12 text-center text-slate-400">Loading…</div>;
    const { assignment, asset, employee, company, generated_at } = data;
    const outcome = assignment.return_outcome || "Good";

    return (
        <div className="bg-white min-h-screen p-12 font-serif" data-testid="asset-return-print">
            <style>{`@media print { @page { size: A4; margin: 12mm; } body { -webkit-print-color-adjust: exact; } }`}</style>

            <header className="border-b-4 border-slate-900 pb-4 mb-6 flex justify-between">
                <div>
                    <div className="text-xs uppercase tracking-widest text-slate-600">{company.legal_name}</div>
                    <h1 className="text-3xl font-bold mt-1">Asset Return Certificate</h1>
                    <div className="text-sm text-slate-600 mt-1">Iter 83.5 / Sprint 5 · Closure of Custody</div>
                </div>
                <div className="text-right text-xs">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Returned</div>
                    <div className="font-mono">{(assignment.returned_at || generated_at || "").slice(0, 10)}</div>
                </div>
            </header>

            <p className="text-sm mb-4">
                This certificate records the return of the asset described below, originally assigned to{" "}
                <strong>{employee?.full_name}</strong> ({employee?.employee_code}). Return outcome:{" "}
                <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${OUTCOME_TONE[outcome] || ""}`}>{outcome}</span>.
            </p>

            <section className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm border-y border-slate-300 py-4 mb-4">
                <Pair label="Asset Tag" value={asset?.asset_tag} mono />
                <Pair label="Category" value={asset?.category_name_snapshot} />
                <Pair label="Serial #" value={asset?.serial_number || "—"} mono />
                <Pair label="Depreciated Value (at return)" value={fmtMoney(asset?.depreciated_value)} />
                <Pair label="Assigned On" value={(assignment.assigned_at || "").slice(0, 10)} />
                <Pair label="Returned On" value={(assignment.returned_at || "").slice(0, 10)} />
                <Pair label="Outcome" value={outcome} />
                <Pair label="Recommended Recovery" value={fmtMoney(assignment.recommended_recovery)} />
                {assignment.return_reason && (
                    <div className="col-span-2">
                        <div className="text-[10px] uppercase tracking-widest text-slate-500">Reason</div>
                        <div className="italic">"{assignment.return_reason}"</div>
                    </div>
                )}
                <Pair label="Assignment ID" value={assignment.id} mono />
                <Pair label="Status" value={assignment.status} />
            </section>

            {outcome !== "Good" && (
                <section className="bg-amber-50 border border-amber-300 rounded p-3 mb-4 text-xs">
                    <h2 className="font-bold mb-1">Recovery Notice (D18)</h2>
                    <p>
                        Per the Asset Return policy (D18), the recommended recovery of{" "}
                        <strong>{fmtMoney(assignment.recommended_recovery)}</strong> may be deducted from the
                        employee's payroll or full-and-final settlement. The actual recovery is determined by
                        Finance during FNF calculation.
                    </p>
                </section>
            )}

            <section className="text-sm mb-4 leading-relaxed">
                <h2 className="font-bold mb-2">Acknowledgement</h2>
                <p className="text-xs">
                    I acknowledge that I have returned the above asset and confirm the inspection outcome.
                    I understand that any recovery amount, if applicable, will be processed through the
                    payroll / FNF settlement workflow.
                </p>
            </section>

            <section className="mt-12 grid grid-cols-2 gap-12">
                <div>
                    <div className="border-t border-slate-700 pt-1 text-xs">Employee Signature · Date</div>
                    <div className="text-[10px] text-slate-500 mt-1">{employee?.full_name}</div>
                </div>
                <div>
                    <div className="border-t border-slate-700 pt-1 text-xs">IT / HR Receiver</div>
                    <div className="text-[10px] text-slate-500 mt-1">For {company.legal_name}</div>
                    <div className="text-[10px] text-slate-500 mt-1">
                        Closed by {assignment.returned_by_email || assignment.returned_by_id || "—"}
                    </div>
                </div>
            </section>
        </div>
    );
}

function Pair({ label, value, mono }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
            <div className={mono ? "font-mono" : ""}>{value || "—"}</div>
        </div>
    );
}
